import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_ENV || 'development';

export function initSentry(appName) {
  // Only initialize if DSN is configured
  if (!SENTRY_DSN) {
    console.log('[Sentry] DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    // Set app context to distinguish between family app and restaurant dashboard
    initialScope: {
      tags: {
        app: appName,
      },
    },

    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Replay for session recording on errors
      Sentry.replayIntegration({
        // Only capture replays on errors
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance monitoring sample rate (10% of transactions)
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    // Session replay sample rate
    replaysSessionSampleRate: 0,  // Don't record all sessions
    replaysOnErrorSampleRate: 1.0, // Record 100% of sessions with errors

    // Don't send errors in development unless DSN is explicitly set
    enabled: !!SENTRY_DSN,

    // Filter out common non-actionable errors
    ignoreErrors: [
      // Network errors that aren't our fault
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // ResizeObserver noise
      'ResizeObserver loop',
    ],

    // Add user context when available
    beforeSend(event) {
      // You can modify or filter events here
      return event;
    },
  });

  console.log(`[Sentry] Initialized for ${appName} (${ENVIRONMENT})`);
}

// Re-export Sentry utilities for use throughout the app
export { Sentry };

// Helper to set user context after login
export function setSentryUser(user) {
  if (user) {
    Sentry.setUser({
      id: user.uid,
      email: user.email || undefined,
    });
  } else {
    Sentry.setUser(null);
  }
}

// Helper to add breadcrumbs for debugging
export function addBreadcrumb(message, category = 'app', data = {}) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Helper to capture custom errors with context
export function captureError(error, context = {}) {
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}
